/**
 * @file src/hooks/useOrganizationSearch.ts
 * @exports useOrganizationSearch
 * @summary Debounced organization search with query-owned pages scoped to one mounted picker.
 */
import { useDebouncedValue } from "@mantine/hooks";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import {
	OrganizationsDocument,
	type OrganizationsQuery,
	type OrganizationsQueryVariables,
	type OrganizationWhereInput,
	QueryMode,
	SortOrder,
} from "src/generated/graphql";
import { useFetchData } from "src/graphql/fetcher";

const PAGE_SIZE = 25;

const NOT_DELETED: OrganizationWhereInput = { deletedAt: { equals: null } };

/** Distinguishes one picker's pages from the next picker's. See the query key below. */
let pickerSessions = 0;

/**
 * Name only. A short name need not appear anywhere in the name it belongs to, so matching on it
 * returned rows with nothing on screen to explain why they were there. Cortex still matches both.
 */
const buildSearchWhere = ({ searchTerm }: { searchTerm: string }): OrganizationWhereInput => {
	if (!searchTerm) return NOT_DELETED;

	return { ...NOT_DELETED, name: { contains: searchTerm, mode: QueryMode.Insensitive } };
};

/** Paged, debounced organization search for one mounted picker. */
export const useOrganizationSearch = ({ searchTerm }: { searchTerm: string }) => {
	const [sessionId] = useState(() => (pickerSessions += 1));
	const [debouncedTerm] = useDebouncedValue(searchTerm.trim(), 300);
	const fetchOrganizations = useFetchData<OrganizationsQuery, OrganizationsQueryVariables>(OrganizationsDocument);
	const query = useInfiniteQuery({
		// Pages belong to the picker that fetched them. A picker that has paged and is
		// then reopened must start from page one, so each mount gets its own key rather
		// than resubscribing to the pages the last one left behind.
		queryKey: ["organizationSearch", sessionId, debouncedTerm],
		cacheTime: 0,
		queryFn: ({ pageParam = 0 }) =>
			fetchOrganizations({
				orderBy: [{ name: SortOrder.Asc }, { id: SortOrder.Asc }],
				where: buildSearchWhere({ searchTerm: debouncedTerm }),
				take: PAGE_SIZE + 1,
				skip: pageParam,
			}),
		getNextPageParam: (lastPage, pages) =>
			lastPage.organizations.length > PAGE_SIZE ? pages.length * PAGE_SIZE : undefined,
	});
	const organizations = useMemo(() => {
		const pages = query.data?.pages ?? [];
		const rows = pages.flatMap((page) => page.organizations);
		return Array.from(new Map(rows.map((org) => [org.id, org])).values()).slice(0, pages.length * PAGE_SIZE);
	}, [query.data]);
	const isFetching = query.isFetching || searchTerm.trim() !== debouncedTerm;

	return {
		/** The term the listed rows actually belong to, which lags the input by the debounce. */
		activeTerm: debouncedTerm,
		hasMore: query.hasNextPage ?? false,
		isError: query.isError,
		isFetching,
		loadMore: query.fetchNextPage,
		organizations,
	};
};
